import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tables } from '../../../database.types';
import { usePlanLimitations } from '@/hooks/usePlanLimitations';
import { PlanLimitAlert } from '@/components/plan/PlanLimitAlert';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { PlusCircle, ShieldCheckIcon, BrainIcon, Settings, Users, Search, Sparkles, TrendingUp, Zap, Star, Building2, CheckCircle2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useBrands } from '@/lib/BrandsProvider';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAccountStatus } from '@/lib/AccountProvider';

type BrandStatus = 'ACTIVE' | 'INACTIVE' | 'DELETED';

type Brand = Omit<Tables<'brands'>, 'status'> & {
    status: BrandStatus;
    notify_on_convergences: boolean;
    convergences: { count: number }[];
    campaigns: { count: number }[];
    contents: { count: number }[];
    access_source?: 'account' | 'direct' | 'both';
    direct_access_level?: 'owner' | 'editor' | 'viewer';
    account_name?: string;
};

export const Brands: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const { limitations, getRemainingLimits, isActionAllowed } = usePlanLimitations();
    const { setSelectedBrand, selectedBrand, refreshBrands: refreshBrandsProvider } = useBrands();
    const { status: accountStatus, loading: accountLoading } = useAccountStatus();
    const isInitialMount = useRef(true);
    
    // Debug account status
    useEffect(() => {
        console.log('Account Status Debug:', {
            accountStatus,
            accountLoading,
            personalAccount: accountStatus?.personalAccount,
            effectiveStatus: accountStatus?.effectiveStatus
        });
    }, [accountStatus, accountLoading]);

    useEffect(() => {
        fetchBrands();
        // Add debugging for subscription and plan data
        checkSubscriptionAndPlan();
    }, []);

    // Refresh brands when navigating to this page (but not on initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else if (location.pathname === '/brands') {
            // Refresh brands list when navigating back to the brands page
            fetchBrands();
            // Also refresh the BrandsProvider if available
            if (refreshBrandsProvider) {
                refreshBrandsProvider();
            }
        }
    }, [location.pathname, refreshBrandsProvider]);

    const checkSubscriptionAndPlan = async () => {
        try {
            // Check active subscriptions
            const { data: subscriptions, error: subError } = await supabase
                .schema('basejump')
                .from('billing_subscriptions')
                .select('*')
                .in('status', ['active', 'trialing']);
            
            console.log('Active Subscriptions:', subscriptions, 'Error:', subError);

            // Check plan limitations
            const { data: planLimits, error: planError } = await supabase
                .schema('basejump')
                .from('plan_limitations')
                .select('*');
            
            console.log('Plan Limitations:', planLimits, 'Error:', planError);

            // Get current account
            const { data: account } = await supabase.rpc('get_personal_account');
            console.log('Current Account:', account);

            // Get plan limitations directly
            const { data: currentPlan } = await supabase.rpc('get_plan_limitations');
            console.log('Current Plan Limitations:', currentPlan);
        } catch (error) {
            console.error('Error checking subscription data:', error);
        }
    };

    useEffect(() => {
        if (limitations) {
            console.log('Current Plan Limitations:', {
                maxBrands: limitations.max_brands,
                planId: limitations.plan_id,
                allLimitations: limitations
            });
        }
        // const remainingLimits = getRemainingLimits();
        // console.log('Remaining Limits:', remainingLimits);
    }, [limitations, getRemainingLimits]);

    const handleCreateBrand = async () => {
        // First check if user has a personal account
        if (!accountStatus?.personalAccount) {
            toast({
                title: "Account Required",
                description: "You need an active account to create brands. Please activate your account in settings.",
                variant: "destructive"
            });
            navigate('/settings');
            return;
        }

        console.log('Checking if brand creation is allowed...');
        const isAllowed = await isActionAllowed('create_brand');
        console.log('Brand creation allowed:', isAllowed);
        
        if (isAllowed) {
            try {
                // Get the user's account ID
                const { data: accountData, error: accountError } = await supabase
                    .rpc('get_personal_account') as { data: { account_id: string } | null, error: any };

                if (accountError || !accountData?.account_id) {
                    throw new Error('Failed to get account information');
                }

                // Create a temporary brand
                const { data: brandData, error: brandError } = await supabase
                    .from('brands')
                    .insert({
                        brand_name: "New Brand",
                        status: 'ACTIVE',
                        account_id: accountData.account_id,
                        elevator_pitch: "",
                        niche: "",
                        url: "",
                        default_landing_page: "" // Will be set when URL is provided during onboarding
                    })
                    .select()
                    .single();

                if (brandError) {
                    throw brandError;
                }

                if (!brandData) {
                    throw new Error('Failed to create new brand');
                }

                // Select the new brand
                setSelectedBrand(brandData as Brand);
                localStorage.setItem('selectedBrandId', brandData.id);

                // Navigate to onboarding with the new brand ID
                navigate(`/onboarding?skipBilling=true&brandId=${brandData.id}`);
            } catch (error) {
                console.error('Error creating new brand:', error);
                toast({
                    title: "Error Creating Brand",
                    description: "Failed to create a new brand. Please try again.",
                    variant: "destructive"
                });
            }
        } else {
            const remainingLimits = getRemainingLimits();
            const maxBrands = limitations?.max_brands || 0;
            
            console.log('Brand creation denied:', {
                currentLimits: limitations,
                remainingLimits,
                maxBrands
            });
            
            toast({
                title: "Brand Limit Reached",
                description: `You've reached your limit of ${maxBrands} brand${maxBrands === 1 ? '' : 's'} on your current plan. Please upgrade your plan to create more brands.`,
                variant: "destructive"
            });
        }
    };

    const fetchBrands = async () => {
        try {
            // Get current user first
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            // Fetch brands the user has access to through their account
            const { data: accountBrands, error: accountError } = await supabase
                .from('brands')
                .select(`
                    *,
                    convergences:convergences(count),
                    campaigns:campaigns(count),
                    contents:contents(count)
                `)
                .neq('status', 'DELETED')
                .order('created_at', { ascending: false });

            if (accountError) throw accountError;

            // Fetch brands the user has direct access to (excluding DELETED brands)
            const { data: directAccessBrands, error: directError } = await supabase
                .from('brand_access')
                .select(`
                    access_level,
                    expires_at,
                    brand:brands!inner(
                        id,
                        brand_name,
                        account_id,
                        status,
                        notify_on_convergences,
                        elevator_pitch,
                        niche,
                        convergences:convergences(count),
                        campaigns:campaigns(count),
                        contents:contents(count)
                    )
                `)
                .eq('user_id', user.id)
                .neq('brand.status', 'DELETED')
                .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

            if (directError) throw directError;

            // Combine both sets of brands
            const brandMap = new Map();
            
            // Add account brands
            accountBrands?.forEach(brand => {
                brandMap.set(brand.id, brand);
            });

            // Add or update with direct access brands (double-check status isn't DELETED)
            directAccessBrands?.forEach(access => {
                const brand = access.brand;
                if (!brandMap.has(brand.id) && brand.status !== 'DELETED') {
                    // This is a brand from another account
                    brandMap.set(brand.id, {
                        ...brand,
                        _directAccessOnly: true,
                        _accessLevel: access.access_level
                    });
                }
            });

            const brandsData = Array.from(brandMap.values());

            // Check for direct access - using the user from above
            const { data: directAccess } = await supabase
                .from('brand_access')
                .select('brand_id, access_level')
                .eq('user_id', user.id)
                .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

            // Map brands with access information
            const brandsWithAccess = brandsData?.map(brand => {
                const hasDirect = directAccess?.find(a => a.brand_id === brand.id);
                const accessSource = hasDirect ? (brand.account_id ? 'both' : 'direct') : 'account';
                
                return {
                    ...brand,
                    status: brand.status === null || brand.status === undefined ? 'INACTIVE' : brand.status as BrandStatus,
                    notify_on_convergences: brand.notify_on_convergences ?? false,
                    convergences: brand.convergences || [{ count: 0 }],
                    campaigns: brand.campaigns || [{ count: 0 }],
                    contents: brand.contents || [{ count: 0 }],
                    access_source: accessSource,
                    direct_access_level: hasDirect?.access_level as 'owner' | 'editor' | 'viewer' | undefined,
                    account_name: undefined // TODO: Fetch account names separately if needed
                };
            }) as Brand[] || [];

            console.log('Fetched brands with access:', brandsWithAccess);
            setBrands(brandsWithAccess);
        } catch (error) {
            console.error('Error fetching brands:', error);
            toast({
                title: "Error",
                description: "Failed to fetch brands. Please try again.",
                variant: "destructive"
            });
        }
    };

    const toggleBrandStatus = async (id: string, currentStatus: BrandStatus) => {
        // Skip if the brand is deleted
        if (currentStatus === 'DELETED') return;
        
        try {
            const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
            console.log('🔍 DEBUG: Toggling brand status:', { id, currentStatus, newStatus });
            
            const { error } = await supabase
                .from('brands')
                .update({ 
                    status: newStatus,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select();

            if (error) {
                console.error('🔍 DEBUG: Database error:', error);
                throw error;
            }

            setBrands(prevBrands => prevBrands.map(brand =>
                brand.id === id ? { ...brand, status: newStatus as BrandStatus } : brand
            ));

            // If brand is being re-activated, trigger convergence processing
            if (newStatus === 'ACTIVE') {
                console.log('🔍 DEBUG: Brand activated, triggering convergence processing for brand ID:', id);
                
                const workerUrl = import.meta.env.VITE_WORKERS_URL;
                const endpoint = `${workerUrl}/rainfactory/brand-genius/process_updated_brand`;
                console.log('🔍 DEBUG: Calling endpoint:', endpoint);
                
                try {
                    const session = await supabase.auth.getSession();
                    console.log('🔍 DEBUG: Auth session available:', !!session.data.session);
                    
                    const response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': session.data.session ? `Bearer ${session.data.session.access_token}` : '',
                        },
                        body: JSON.stringify({
                            brandId: id
                        })
                    });
                    
                    const responseData = await response.json();
                    console.log('🔍 DEBUG: Process updated brand response:', responseData);
                    
                    if (!response.ok) {
                        console.error('🔍 DEBUG: Error response from process_updated_brand:', response.status, responseData);
                    }
                } catch (fetchError) {
                    console.error('🔍 DEBUG: Error calling process_updated_brand:', fetchError);
                }
            }
        } catch (error) {
            console.error('🔍 DEBUG: Error updating brand status:', error);
            toast({
                title: "Error",
                description: "Failed to update brand status. Please try again.",
                variant: "destructive"
            });
        }
    };

    const toggleConvergenceNotifications = async (id: string, currentValue: boolean) => {
        try {
            const { error } = await supabase
                .from('brands')
                .update({ 
                    notify_on_convergences: !currentValue,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);
            if (error) throw error;
            setBrands(prevBrands => prevBrands.map(brand =>
                brand.id === id ? { ...brand, notify_on_convergences: !currentValue } : brand
            ));
            
            toast({
                title: !currentValue ? "Notifications enabled" : "Notifications disabled",
                description: !currentValue 
                    ? "You'll be notified when new convergences are available" 
                    : "You won't be notified about new convergences",
            });
        } catch (error) {
            console.error('Error updating notification preference:', error);
            toast({
                title: "Error",
                description: "Failed to update notification preference. Please try again.",
                variant: "destructive"
            });
        }
    };

    const filteredBrands = brands
        .filter(brand =>
            brand.brand_name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => a.brand_name.localeCompare(b.brand_name));

    const remainingLimits = getRemainingLimits();
    const isAtLimit = remainingLimits ? remainingLimits.remaining_brands <= 0 : false;
    const canAddBrand = !isAtLimit && !!(accountStatus && accountStatus.personalAccount);

    return (
        <section className="p-6 space-y-6">
            <PageHeader
                title="Your Brands"
                description={
                    <div className="space-y-1">
                        {accountStatus && !accountStatus.personalAccount ? (
                            <>
                                <p>You currently have access to shared brands only.</p>
                                <p className="text-sm text-muted-foreground">
                                    You can't create brands without an active account.{' '}
                                    <Button
                                        variant="link"
                                        className="p-0 h-auto text-sm"
                                        onClick={() => navigate('/settings')}
                                    >
                                        Click here to activate your account
                                    </Button>
                                </p>
                            </>
                        ) : (
                            <>
                                <p>Create and manage your brands to organize your marketing campaigns and content.</p>
                                {!limitations ? null : (
                                    <p className="text-sm text-muted-foreground">
                                        You can create up to {limitations.max_brands} brand{limitations.max_brands === 1 ? '' : 's'} on your current plan.
                                        {remainingLimits && ` You have ${remainingLimits.remaining_brands} remaining.`}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                }
                icon={<ShieldCheckIcon className="h-6 w-6" />}
                actions={!canAddBrand ? (
                    <Button
                        onClick={handleCreateBrand}
                        disabled={isAtLimit || (accountStatus && !accountStatus.personalAccount) || false}
                    >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        New Brand
                    </Button>
                ) : null}
            />

            {/* Account Status Display */}
            {!accountLoading && accountStatus && (
                <div className="relative overflow-hidden bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 border border-blue-200/50 rounded-xl p-6 mb-6">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-white/80 backdrop-blur-sm rounded-lg">
                                <Building2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Account Status</h3>
                                <div className="flex items-center gap-3">
                                    <Badge 
                                        variant={
                                            accountStatus.effectiveStatus === 'active' ? 'default' :
                                            accountStatus.effectiveStatus === 'needs_billing' ? 'destructive' :
                                            'secondary'
                                        }
                                        className="shadow-sm font-medium"
                                    >
                                        {accountStatus.effectiveStatus === 'active' && <Star className="h-3 w-3 mr-1" />}
                                        {accountStatus.effectiveStatus === 'active' ? 'Active' :
                                         accountStatus.effectiveStatus === 'needs_billing' ? 'Billing Required' :
                                         'Inactive Brands Only'}
                                    </Badge>
                                    {accountStatus.personalAccount && (
                                        <span className="text-xs text-gray-600 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-md">
                                            Personal Account: {accountStatus.personalAccount.status}
                                            {accountStatus.personalAccount.subscription && 
                                             accountStatus.personalAccount.subscription.current_period_end && (
                                                <>
                                                    {' • '}
                                                    {accountStatus.personalAccount.subscription.cancel_at_period_end 
                                                        ? `Cancels ${new Date(accountStatus.personalAccount.subscription.current_period_end).toLocaleDateString()}`
                                                        : `Renews ${new Date(accountStatus.personalAccount.subscription.current_period_end).toLocaleDateString()}`
                                                    }
                                                </>
                                            )}
                                        </span>
                                    )}
                                    {!accountStatus.personalAccount && accountStatus.hasActiveBrands && (
                                        <span className="text-xs text-gray-600 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-md">
                                            Access through shared brands only
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {accountStatus.requiresBilling && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="bg-white/80 backdrop-blur-sm hover:bg-white shadow-sm"
                                onClick={() => navigate('/settings/billing')}
                            >
                                <Zap className="h-4 w-4 mr-2" />
                                Update Billing
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {isAtLimit && (
                <PlanLimitAlert type="brands" className="mb-4" />
            )}

            <div className="space-y-6">
                {/* Enhanced Search Section */}
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search your brands..."
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 placeholder:text-gray-500"
                    />
                    {searchQuery && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <button
                                onClick={() => setSearchQuery('')}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>

                {filteredBrands.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                        {canAddBrand && (
                            <div
                                key="add-brand"
                                className={cn(
                                    "group relative overflow-hidden bg-white rounded-2xl border-2 border-dashed border-blue-300 hover:border-blue-500 shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 p-6 flex items-center justify-center cursor-pointer"
                                )}
                                onClick={handleCreateBrand}
                                title="Add a new brand"
                            >
                                <div className="flex flex-col items-center text-blue-700">
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
                                        <PlusCircle className="h-6 w-6" />
                                    </div>
                                    <span className="font-semibold">Add Brand</span>
                                    {limitations && (
                                        <span className="text-xs text-muted-foreground mt-1">
                                            {remainingLimits && remainingLimits.remaining_brands >= 0
                                                ? `You can add ${remainingLimits.remaining_brands} more`
                                                : `Create a new brand`}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        {filteredBrands.map((brand, index) => (
                            <div 
                                key={brand.id} 
                                className={cn(
                                    "group relative overflow-hidden bg-gradient-to-br from-white via-gray-50/50 to-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 p-6 space-y-5 animate-brand-slide cursor-pointer",
                                    selectedBrand?.id === brand.id 
                                        ? "border-2 border-emerald-400 shadow-lg shadow-emerald-100/50 hover:shadow-emerald-200/50" 
                                        : "border border-gray-200/60 hover:shadow-blue-100/50 hover:border-blue-200"
                                )}
                                style={{
                                    animationDelay: `${index * 100}ms`
                                }}
                                onClick={() => {
                                    setSelectedBrand(brand);
                                    localStorage.setItem('selectedBrandId', brand.id);
                                    toast({
                                        title: "Brand Selected",
                                        description: `${brand.brand_name} is now your active brand.`,
                                    });
                                }}
                            >
                                {/* Decorative gradient overlay */}
                                <div className={cn(
                                    "absolute inset-0 bg-gradient-to-br transition-opacity duration-500",
                                    selectedBrand?.id === brand.id 
                                        ? "from-emerald-50/30 via-transparent to-emerald-50/30 opacity-100" 
                                        : "from-blue-50/20 via-transparent to-purple-50/20 opacity-0 group-hover:opacity-100"
                                )} />
                                
                                {/* Animated border glow */}
                                <div className={cn(
                                    "absolute inset-0 rounded-2xl transition-opacity duration-500 blur-sm",
                                    selectedBrand?.id === brand.id 
                                        ? "bg-gradient-to-r from-emerald-400/20 via-emerald-400/20 to-emerald-400/20 opacity-100" 
                                        : "bg-gradient-to-r from-blue-400/10 via-purple-400/10 to-indigo-400/10 opacity-0 group-hover:opacity-100"
                                )} />
                                {/* Header section with brand avatar */}
                                <div className="relative flex flex-col space-y-4">
                                    <div className="flex items-start gap-4">
                                        {/* Brand Avatar */}
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                                                <span className="text-white font-bold text-lg">
                                                    {brand.brand_name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className={cn(
                                                    "text-xl font-bold truncate transition-colors duration-300",
                                                    selectedBrand?.id === brand.id 
                                                        ? "text-emerald-700" 
                                                        : "text-gray-900 group-hover:text-blue-700"
                                                )}>
                                                    {brand.brand_name}
                                                </h3>
                                                {selectedBrand?.id === brand.id && (
                                                    <div className="flex-shrink-0">
                                                        <CheckCircle2 className="h-5 w-5 text-emerald-600 animate-pulse" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                <Badge 
                                                    variant={brand.status === 'ACTIVE' ? 'default' : 'secondary'}
                                                    className={`shadow-sm font-medium transition-all duration-300 ${
                                                        brand.status === 'ACTIVE' 
                                                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-200' 
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {brand.status === 'ACTIVE' && <div className="w-2 h-2 bg-emerald-500 rounded-full mr-1.5 animate-pulse" />}
                                                    {brand.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                                                </Badge>
                                                {brand.access_source === 'direct' && (
                                                    <Badge variant="outline" className="text-xs flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200">
                                                        <Users className="h-3 w-3" />
                                                        Direct - {brand.direct_access_level}
                                                    </Badge>
                                                )}
                                                {brand.access_source === 'both' && (
                                                    <Badge variant="outline" className="text-xs flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200">
                                                        <Users className="h-3 w-3" />
                                                        Team + Direct - {brand.direct_access_level}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Settings toggles with modern switches */}
                                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="h-4 w-4 text-blue-500" />
                                                <span className="text-sm font-medium text-gray-700">Notifications</span>
                                            </div>
                                            <Switch
                                                checked={brand.notify_on_convergences}
                                                onCheckedChange={() => toggleConvergenceNotifications(brand.id, brand.notify_on_convergences)}
                                                className="data-[state=checked]:bg-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Zap className="h-4 w-4 text-emerald-500" />
                                                <span className="text-sm font-medium text-gray-700">Active Status</span>
                                            </div>
                                            <Switch
                                                checked={brand.status === 'ACTIVE'}
                                                onCheckedChange={() => toggleBrandStatus(brand.id, brand.status)}
                                                className="data-[state=checked]:bg-emerald-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Description section */}
                                <div className="relative space-y-3">
                                    <div className="space-y-2">
                                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-2 min-h-[2.5rem]">
                                            {brand.elevator_pitch || 'No description provided yet.'}
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                                            <p className="text-sm text-gray-500 font-medium line-clamp-1">
                                                {brand.niche || 'General'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced metrics section */}
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 rounded-xl opacity-50" />
                                    <div className="relative grid grid-cols-3 gap-4 p-4 rounded-xl border border-gray-100/50">
                                        <div className="text-center group/metric hover:scale-105 transition-transform duration-200">
                                            <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg mb-2 group-hover/metric:bg-blue-200 transition-colors">
                                                <TrendingUp className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <span className="block text-lg font-bold text-gray-900 group-hover/metric:text-blue-700 transition-colors">
                                                {brand.convergences[0]?.count || 0}
                                            </span>
                                            <span className="text-xs text-gray-600 font-medium">Convergences</span>
                                        </div>
                                        <div className="text-center group/metric hover:scale-105 transition-transform duration-200">
                                            <div className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg mb-2 group-hover/metric:bg-purple-200 transition-colors">
                                                <Zap className="h-4 w-4 text-purple-600" />
                                            </div>
                                            <span className="block text-lg font-bold text-gray-900 group-hover/metric:text-purple-700 transition-colors">
                                                {brand.campaigns[0]?.count || 0}
                                            </span>
                                            <span className="text-xs text-gray-600 font-medium">Campaigns</span>
                                        </div>
                                        <div className="text-center group/metric hover:scale-105 transition-transform duration-200">
                                            <div className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-lg mb-2 group-hover/metric:bg-indigo-200 transition-colors">
                                                <Sparkles className="h-4 w-4 text-indigo-600" />
                                            </div>
                                            <span className="block text-lg font-bold text-gray-900 group-hover/metric:text-indigo-700 transition-colors">
                                                {brand.contents[0]?.count || 0}
                                            </span>
                                            <span className="text-xs text-gray-600 font-medium">Content</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced footer section with action buttons */}
                                <div className="relative pt-4 border-t border-gradient-to-r from-gray-100 via-gray-200 to-gray-100">
                                    <div className="flex gap-2 justify-center">
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedBrand(brand);
                                                localStorage.setItem('selectedBrandId', brand.id);
                                                navigate('/brand-genius');
                                            }}
                                            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105 flex-1 sm:flex-none"
                                            title="Open Brand Genius"
                                        >
                                            <BrainIcon className="h-4 w-4" />
                                            <span className="hidden xs:inline sm:hidden md:inline">Brand Genius</span>
                                            <span className="xs:hidden sm:inline md:hidden">Genius</span>
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedBrand(brand);
                                                localStorage.setItem('selectedBrandId', brand.id);
                                                navigate(`/brands/${brand.id}/settings`);
                                            }}
                                            className="flex items-center gap-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105 flex-1 sm:flex-none"
                                            title="Brand Settings"
                                        >
                                            <Settings className="h-4 w-4" />
                                            <span className="hidden xs:inline sm:hidden md:inline">Settings</span>
                                        </Button>
                                    </div>
                                    {selectedBrand?.id === brand.id && (
                                        <div className="mt-3 text-center">
                                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                                                <CheckCircle2 className="h-4 w-4" />
                                                <span>Active Brand</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30 rounded-2xl border border-gray-200/60 shadow-sm">
                        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                        <div className="relative text-center py-16 px-8">
                            {/* Decorative elements */}
                            <div className="absolute top-8 left-8 w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full opacity-50 animate-pulse" />
                            <div className="absolute bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-indigo-100 to-pink-100 rounded-full opacity-30 animate-pulse" style={{ animationDelay: '1s' }} />
                            
                            {/* Icon */}
                            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                                {searchQuery ? (
                                    <Search className="h-10 w-10 text-white" />
                                ) : (
                                    <Sparkles className="h-10 w-10 text-white" />
                                )}
                            </div>
                            
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">
                                {searchQuery ? 'No Matching Brands' : 'No Brands Yet'}
                            </h3>
                            
                            <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                                {searchQuery 
                                    ? "We couldn't find any brands matching your search. Try adjusting your search terms." 
                                    : accountStatus && !accountStatus.personalAccount
                                        ? "You need an active account to create and manage your own brands."
                                        : "Create your first brand to start building amazing marketing campaigns and content."}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                {searchQuery && (
                                    <Button
                                        onClick={() => setSearchQuery('')}
                                        variant="outline"
                                        className="bg-white hover:bg-gray-50 border-gray-200"
                                    >
                                        Clear Search
                                    </Button>
                                )}
                                
                                {!searchQuery && !isAtLimit && accountStatus?.personalAccount && (
                                    <Button
                                        onClick={handleCreateBrand}
                                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                    >
                                        <PlusCircle className="mr-2 h-5 w-5" />
                                        Create Your First Brand
                                    </Button>
                                )}
                                
                                {!searchQuery && accountStatus && !accountStatus.personalAccount && (
                                    <Button
                                        onClick={() => navigate('/settings')}
                                        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                                    >
                                        <Star className="mr-2 h-5 w-5" />
                                        Activate Your Account
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
